import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import moment from "moment";
import * as XLSX from "xlsx";

// --- PDF IMPORTS (FIXED) ---
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { ButtonGroup, Dropdown } from "react-bootstrap";
import { FaCalendarAlt } from "react-icons/fa";
import {
  RiDownloadLine,
  RiFileExcelLine,
  RiFilePdfLine,
  RiPrinterLine,
} from "react-icons/ri";
import "react-datepicker/dist/react-datepicker.css";

// --- CUSTOM CONFIGURATION PATHS ---
import config from "../../../Common/Configurations/APIConfig";
import apiService from "../../../Common/Configurations/APIService";
import { useAuth } from "../../../Common/Configurations/AuthContext";

const ExpenseReport = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // --- STATE MANAGEMENT ---
  const [reports, setReports] = useState([]);
  const [fromDate, setFromDate] = useState(moment().startOf("month").toDate());
  const [toDate, setToDate] = useState(moment().endOf("month").toDate());
  const [status, setStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [itemSuggestions, setItemSuggestions] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("all");

  // --- HELPERS ---
  const getUserCurrency = () => {
    return user?.currency || "INR";
  };

  const getStatusBadge = (status) => {
    if (status === "PAID")
      return <span className="badge bg-success">PAID</span>;
    if (status === "UNPAID")
      return <span className="badge bg-danger">UNPAID</span>;
    return <span className="badge bg-primary">DRAFT</span>;
  };

  const calculateTotal = () => {
    return reports.reduce((sum, report) => sum + report.grandTotal, 0);
  };

  // --- API CALLS ---
  const GetBranches = async () => {
    try {
      const res = await apiService.getAll(
        `${config.GETBRANCHBYCOMPANY}/${user?.cid}`
      );
      setBranches(res.data || res);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const GetReports = async () => {
    try {
      const params = new URLSearchParams();
      params.append("userId", user.id);

      if (selectedBranch && selectedBranch !== "all") {
        params.append("branchId", selectedBranch);
      }
      if (fromDate) {
        params.append("fromDate", moment(fromDate).format("YYYY-MM-DD"));
      }
      if (toDate) {
        params.append("toDate", moment(toDate).format("YYYY-MM-DD"));
      }
      if (status && status !== "all") {
        params.append("status", status);
      }
      if (selectedItem) {
        params.append("itemName", selectedItem.itemName);
      }

      const res = await apiService.getAll(
        `${config.EXPENSEREPORT}/${user.id}?${params.toString()}`
      );
      setReports(res);
    } catch (error) {
      console.error("Error fetching reports:", error);
      setReports([]);
    }
  };

  const searchItems = async (keyword) => {
    if (!keyword) {
      setItemSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      const res = await apiService.getAll(
        `${config.SEARCHEXPENSEITEMS}?Keyword=${keyword}`
      );
      setItemSuggestions(res);
      setShowSuggestions(true);
    } catch (error) {
      console.error("Error searching items:", error);
    }
  };

  // --- EXPORT & PRINT FUNCTIONS ---
  const exportToExcel = () => {
    const excelData = reports.map((report) => ({
      "Invoice No": report.invoiceNumber,
      "Item Name": report.itemName || "",
      "Invoice Date": moment(report.invoiceDate).format("DD-MMM-YYYY"),
      "Due Date": moment(report.dueDate).format("DD-MMM-YYYY"),
      Status: report.status,
      Amount: report.grandTotal.toFixed(2),
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const header = [
      "Invoice No",
      "Item Name",
      "Invoice Date",
      "Due Date",
      "Status",
      "Amount",
    ];
    XLSX.utils.sheet_add_aoa(worksheet, [header], { origin: "A1" });

    XLSX.utils.sheet_add_aoa(
      worksheet,
      [["", "", "", "", "Total:", calculateTotal().toFixed(2)]],
      { origin: -1 }
    );

    const wscols = [
      { wch: 20 },
      { wch: 30 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
    ];
    worksheet["!cols"] = wscols;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Expense Reports");
    XLSX.writeFile(
      workbook,
      `Expense_Report_${moment().format("YYYYMMDD")}.xlsx`
    );
  };

  const exportToPDF = () => {
    const currency = getUserCurrency();
    const doc = new jsPDF();

    // Title
    doc.setFontSize(16);
    doc.setTextColor(44, 62, 80);
    doc.text("EXPENSE REPORT", 105, 15, { align: "center" });

    // Filters text
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    let filtersText = "Filters: ";
    if (selectedBranch && selectedBranch !== "all") {
      const branchName =
        branches.find((b) => b.branchId === selectedBranch)?.branchName ||
        selectedBranch;
      filtersText += `Branch: ${branchName} `;
    }
    if (fromDate)
      filtersText += `From: ${moment(fromDate).format("DD-MMM-YYYY")} `;
    if (toDate) filtersText += `To: ${moment(toDate).format("DD-MMM-YYYY")} `;

    doc.text(filtersText, 14, 25);
    doc.text(`Generated on: ${moment().format("DD-MMM-YYYY hh:mm A")}`, 14, 30);

    // Table Data
    const tableData = reports.map((report) => [
      report.invoiceNumber,
      report.itemName || "",
      moment(report.invoiceDate).format("DD-MMM-YYYY"),
      moment(report.dueDate).format("DD-MMM-YYYY"),
      report.status,
      `${currency} ${report.grandTotal.toFixed(2)}`,
    ]);

    // Total Row
    tableData.push([
      "",
      "",
      "",
      "",
      "Total:",
      `${currency} ${calculateTotal().toFixed(2)}`,
    ]);

    // PDF Table Generation (FIXED METHOD)
    autoTable(doc, {
      head: [
        [
          "Invoice No",
          "Item Name",
          "Invoice Date",
          "Due Date",
          "Status",
          "Amount",
        ],
      ],
      body: tableData,
      startY: 35,
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: "bold",
      },
      columnStyles: { 5: { halign: "right" } },
      didParseCell: function (data) {
        if (data.row.index === tableData.length - 1) {
          data.cell.styles.fontStyle = "bold";
        }
      },
    });

    doc.save(`Expense_Report_${moment().format("YYYYMMDD")}.pdf`);
  };

  const handlePrint = () => {
    const currency = getUserCurrency();
    const printWindow = window.open("", "_blank");
    const printContent = `
    <html>
      <head>
        <title>Expense Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { color: #2c3e50; text-align: center; }
          .filters { margin-bottom: 20px; font-size: 14px; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th { background-color: #2980b9; color: white; padding: 8px; text-align: left; }
          td { padding: 6px; border-bottom: 1px solid #ddd; }
          .text-right { text-align: right; }
          .total-row { font-weight: bold; }
        </style>
      </head>
      <body>
        <h1>EXPENSE REPORT</h1>
        <div class="filters">Generated on: ${moment().format(
          "DD-MMM-YYYY hh:mm A"
        )}</div>
        <table>
          <thead>
            <tr>
              <th>Invoice No</th>
              <th>Item Name</th>
              <th>Invoice Date</th>
              <th>Due Date</th>
              <th>Status</th>
              <th class="text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${reports
              .map(
                (report) => `
              <tr>
                <td>${report.invoiceNumber}</td>
                <td>${report.itemName || ""}</td>
                <td>${moment(report.invoiceDate).format("DD-MMM-YYYY")}</td>
                <td>${moment(report.dueDate).format("DD-MMM-YYYY")}</td>
                <td>${report.status}</td>
                <td class="text-right">${currency} ${report.grandTotal.toFixed(
                  2
                )}</td>
              </tr>
            `
              )
              .join("")}
            <tr class="total-row">
              <td colspan="5" class="text-right">Total:</td>
              <td class="text-right">${currency} ${calculateTotal().toFixed(
      2
    )}</td>
            </tr>
          </tbody>
        </table>
        <script>
          window.onload = function() { setTimeout(function() { window.print(); window.close(); }, 200); };
        </script>
      </body>
    </html>
  `;
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  // --- HANDLERS ---
  const handleItemSelect = (item) => {
    setSelectedItem(item);
    setSearchQuery(item.itemName);
    setItemSuggestions([]);
    setShowSuggestions(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    GetReports();
  };

  const handleClear = () => {
    setFromDate(moment().startOf("month").toDate());
    setToDate(moment().endOf("month").toDate());
    setStatus("all");
    setSearchQuery("");
    setSelectedItem(null);
    setItemSuggestions([]);
    setSelectedBranch("all");
  };

  // --- USE EFFECT ---
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (user && !loading) {
      GetBranches();
      GetReports();
    }
  }, [user, loading, fromDate, toDate, status, selectedItem, selectedBranch]);

  // --- RENDER ---
  if (loading) return <div className="p-4 text-center">Loading...</div>;
  if (!user) {
    navigate("/");
    return null;
  }

  return (
    <div className="container-fluid p-4">
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm">
            {/* Header with Title and Buttons */}
            <div className="card-header bg-white border-bottom-0 d-flex justify-content-between align-items-center">
              <h4 className="card-title mb-0">Expense Report</h4>
              <div className="d-flex">
                <Dropdown as={ButtonGroup} className="me-2">
                  <Dropdown.Toggle
                    variant="primary"
                    className="btn btn-primary d-flex align-items-center gap-1"
                  >
                    <RiDownloadLine /> Export
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item
                      onClick={exportToExcel}
                      className="d-flex align-items-center gap-2"
                    >
                      <RiFileExcelLine className="text-success" /> Excel
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={exportToPDF}
                      className="d-flex align-items-center gap-2"
                    >
                      <RiFilePdfLine className="text-danger" /> PDF
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
                <button
                  className="btn btn-secondary d-flex align-items-center gap-1"
                  onClick={handlePrint}
                >
                  <RiPrinterLine /> Print
                </button>
              </div>
            </div>

            <div className="card-body">
              {/* Filters Section */}
              <div className="row mb-3">
                {/* Branch Filter */}
                <div className="col-md-3">
                  <label className="form-label">Branch</label>
                  <select
                    className="form-select"
                    value={selectedBranch}
                    onChange={(e) => setSelectedBranch(e.target.value)}
                  >
                    <option value="all">All Branches</option>
                    {branches.map((branch) => (
                      <option key={branch.branchId} value={branch.branchId}>
                        {branch.branchName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Item Search */}
                <div className="col-md-3">
                  <label className="form-label">Search Item</label>
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Type item name..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        searchItems(e.target.value);
                      }}
                    />
                    {showSuggestions && itemSuggestions.length > 0 && (
                      <div
                        className="position-absolute w-100 bg-white border rounded mt-1 shadow-sm"
                        style={{
                          zIndex: 1000,
                          maxHeight: "200px",
                          overflowY: "auto",
                        }}
                      >
                        <ul className="list-unstyled mb-0">
                          {itemSuggestions.map((item) => (
                            <li
                              key={item.itemId}
                              className="px-3 py-2 cursor-pointer border-bottom hover-bg-light"
                              style={{ cursor: "pointer" }}
                              onClick={() => handleItemSelect(item)}
                            >
                              {item.itemName}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* From Date */}
                <div className="col-md-2">
                  <label className="form-label">From Date</label>
                  <div className="position-relative">
                    <DatePicker
                      selected={fromDate}
                      onChange={(date) => setFromDate(date)}
                      dateFormat="dd-MMM-yyyy"
                      className="form-control"
                      placeholderText="Select Date"
                    />
                    <FaCalendarAlt
                      className="position-absolute end-0 top-50 translate-middle-y me-2 text-muted"
                      style={{ pointerEvents: "none" }}
                    />
                  </div>
                </div>

                {/* To Date */}
                <div className="col-md-2">
                  <label className="form-label">To Date</label>
                  <div className="position-relative">
                    <DatePicker
                      selected={toDate}
                      onChange={(date) => setToDate(date)}
                      minDate={fromDate}
                      dateFormat="dd-MMM-yyyy"
                      className="form-control"
                      placeholderText="Select Date"
                    />
                    <FaCalendarAlt
                      className="position-absolute end-0 top-50 translate-middle-y me-2 text-muted"
                      style={{ pointerEvents: "none" }}
                    />
                  </div>
                </div>

                {/* Status Filter */}
                <div className="col-md-2">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <option value="all">All</option>
                    <option value="PAID">PAID</option>
                    <option value="UNPAID">UNPAID</option>
                  </select>
                </div>
              </div>

              {/* Search Buttons */}
              <div className="row mb-4">
                <div className="col-md-12 d-flex justify-content-end">
                  <button
                    className="btn btn-primary me-2"
                    onClick={handleSearch}
                  >
                    Search
                  </button>
                  <button
                    className="btn btn-outline-danger"
                    onClick={handleClear}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>

              {/* Table Section */}
              <div className="table-responsive">
                <table className="table table-bordered table-hover align-middle mb-0">
                  <thead className="bg-soft-success bg-gradient">
                    <tr>
                      <th>Invoice No</th>
                      <th>Item Name</th>
                      <th>Invoice Date</th>
                      <th>Due Date</th>
                      <th>Status</th>
                      <th className="text-end">Grand Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.length > 0 ? (
                      <>
                        {reports.map((report, index) => (
                          <tr key={index}>
                            <td>{report.invoiceNumber}</td>
                            <td>{report.itemName || "-"}</td>
                            <td>
                              {moment(report.invoiceDate).format("DD-MMM-YYYY")}
                            </td>
                            <td>
                              {moment(report.dueDate).format("DD-MMM-YYYY")}
                            </td>
                            <td>{getStatusBadge(report.status)}</td>
                            <td className="text-end">
                              {getUserCurrency()} {report.grandTotal.toFixed(2)}
                            </td>
                          </tr>
                        ))}
                        <tr className="fw-bold bg-light">
                          <td colSpan="5" className="text-end">
                            Total:
                          </td>
                          <td className="text-end">
                            {getUserCurrency()} {calculateTotal().toFixed(2)}
                          </td>
                        </tr>
                      </>
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center py-4 text-muted">
                          No expense records found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseReport;
